import { gql } from 'graphql-request';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { ParsedUrlQuery } from 'querystring';

import MaxWidthContainer from '@/components/dom/max-width-container';
import { Heading } from '@/components/dom/text-elements';
import { TeamAvatar, UserAvatar } from '@/components/global/avatar';
import Icon from '@/components/global/icon';
import IndexStripe from '@/components/user/index-stripe';
import { FormatUtcDate } from '@/helpers/dates';
import { GetOrganizationPublicRedirectPath } from '@/helpers/paths';
import { SITE_NAME } from '@/lib/constants';
import { BUILD_SERVER_SIDE_PROPS_GQL_CLIENT } from '@/lib/graphql';
import { INDEX_CARD_FRAGMENT, INDEX_CARD_FRAGMENT_NAME, IndexCardType } from '@/queries/index-card';

type UserProps = {
    id: string;
    name: string;
    about: string;
    avatarUrl: string;
    followedIndexes: Array<IndexCardType>;
    primaryOrganization: {
        id: string;
        name: string;
        avatarUrl: Optional<string>;
    };
    linkedinUrl: string;
    twitterHandle: string;
};

const USER_QUERY = gql`
    ${INDEX_CARD_FRAGMENT}
    query getUser($id: ID!, $threeMonthsAgo: Date!) {
        user(id: $id) {
            id
            name
            about
            avatarUrl
            twitterHandle
            linkedinUrl
            followedIndexes {
                ...${INDEX_CARD_FRAGMENT_NAME}
            }
            primaryOrganization {
                id
                name
                avatarUrl
            }
        }
    }
`;

const User = ({
    name,
    about,
    avatarUrl,
    followedIndexes,
    primaryOrganization,
    linkedinUrl,
    twitterHandle,
}: UserProps) => {
    const titleText = `${name} | ${SITE_NAME}`;

    return (
        <div className="bg-gradient-to-b from-analyst-lavender from-10% to-analyst-white">
            <Head>
                <title>{titleText}</title>
                <meta
                    property="og:image"
                    content="https://www.gothematic.com/favicon/favicon-32x32.png"
                />
            </Head>

            <MaxWidthContainer className="flex py-40">
                <aside className="flex-grow-0 hidden md:block">
                    <UserAvatar
                        url={avatarUrl}
                        name={name}
                        className="h-48 w-48 text-9xl border border-brand-gray-med"
                    />
                    {about && (
                        <div className="mt-6 break-all text-sm md:w-48 md:break-normal whitespace-pre-wrap mb-6">
                            {about}
                        </div>
                    )}
                    <ul className="text-brand-blue-nu">
                        <li>
                            {twitterHandle && (
                                <Link
                                    href={`https://x.com/${twitterHandle}`}
                                    className="text-brand-blue-nu visited:text-brand-blue-nu no-underline flex align-middle mb-2"
                                >
                                    <Icon
                                        type="xTwitter"
                                        className="text-inherit mr-2 mt-1"
                                    />
                                    X
                                </Link>
                            )}
                        </li>
                        <li>
                            {linkedinUrl && (
                                <Link
                                    href={`https://www.linkedin.com/in/${linkedinUrl}`}
                                    className="text-brand-blue-nu visited:text-brand-blue-nu no-underline flex align-middle"
                                >
                                    <Icon
                                        type="linkedIn"
                                        color="blueNu"
                                        className="text-inherit mr-2 mt-0.5"
                                    />
                                    LinkedIn
                                </Link>
                            )}
                        </li>
                    </ul>
                </aside>
                <main className="ml-6 mt-10 flex-shrink w-full basis-full overflow-hidden">
                    <div className="flex grow justify-between mb-12 h-24">
                        <Heading importance={2}>{name}</Heading>
                        <div className="flex">
                            <TeamAvatar
                                url={primaryOrganization.avatarUrl}
                                name={primaryOrganization.name}
                                className="h-8 w-8 text-xl md:h-16 md:w-16 md:text-6xl mr-2"
                            />
                            <div className="flex flex-col">
                                <span>{primaryOrganization.name}</span>

                                <Link
                                    href={GetOrganizationPublicRedirectPath(primaryOrganization.id)}
                                    className="no-underline"
                                >
                                    Go to team profile page &gt;
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <ul>
                            {followedIndexes.map((index, i) => {
                                return (
                                    <IndexStripe
                                        index={index}
                                        key={`index-card-${i}`}
                                    />
                                );
                            })}
                        </ul>
                    </div>
                </main>
            </MaxWidthContainer>
        </div>
    );
};

// export index page
export default User;

// static build code is found below
interface Params extends ParsedUrlQuery {
    id: string;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { id } = context.params as Params;

    // Three months ago
    const performanceStartDate = new Date();
    performanceStartDate.setMonth(performanceStartDate.getMonth() - 3);

    const client = BUILD_SERVER_SIDE_PROPS_GQL_CLIENT(context.req.headers);
    const data: { user: Optional<UserProps> } = await client.request(USER_QUERY, {
        id: id && id.toUpperCase(),
        threeMonthsAgo: FormatUtcDate(performanceStartDate, 'yyyy-MM-dd'),
    });
    const { user } = data;

    if (!user) {
        return { notFound: true };
    }

    return { props: user };
};
